/**
 * AttachmentStrip — visual row of image previews queued for the next
 * message. Lives ABOVE the textarea (v18 mockup `.composer-attachments`).
 *
 * Each item shows a thumbnail (via blob URL if `previewUrl` provided,
 * otherwise a placeholder) + a hover-revealed X to remove. Removal is
 * delegated to the caller (`useChatAttachments.remove(key)`).
 *
 * v1 ref: `QueryInput.tsx` lines 503-543 — `ImagePreviewStrip`. Same
 * shape; uploadChatImage already returns `{ minio_key, filename, mime_type }`.
 * Optional `previewUrl` is the client-side blob URL the caller can
 * `URL.createObjectURL(file)` for instant feedback before upload completes.
 */

import type * as React from "react";
import { X } from "lucide-react";

import { cn } from "../../lib/cn";

export interface AttachmentImage {
  /** Stable identifier — usually `minio_key`. */
  key: string;
  /** Original filename, used as alt + tooltip. */
  filename: string;
  /** Optional preview URL — local blob URL or remote thumbnail. */
  previewUrl?: string;
}

export interface AttachmentStripProps {
  images: AttachmentImage[];
  onRemove: (key: string) => void;
  className?: string;
}

export const AttachmentStrip: React.FC<AttachmentStripProps> = ({
  images,
  onRemove,
  className,
}) => {
  if (images.length === 0) return null;
  return (
    <div
      className={cn(
        "flex flex-wrap items-start gap-2 px-2.5 pt-2 pb-1",
        className,
      )}
    >
      {images.map((img) => (
        <div
          key={img.key}
          className="group relative h-14 w-14 shrink-0 overflow-hidden rounded-md border border-hairline bg-ink-1/[0.04]"
          title={img.filename}
        >
          {img.previewUrl ? (
            <div
              role="img"
              aria-label={img.filename}
              className="h-full w-full bg-center bg-cover"
              style={{ backgroundImage: `url(${img.previewUrl})` }}
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center font-medium text-[10px] text-ink-3 uppercase">
              IMG
            </div>
          )}
          <button
            type="button"
            aria-label={`Rimuovi ${img.filename}`}
            onClick={() => onRemove(img.key)}
            className="absolute top-0.5 right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-ink-1/80 text-white opacity-0 transition-opacity hover:bg-ink-1 focus-visible:opacity-100 group-hover:opacity-100"
          >
            <X size={10} strokeWidth={2.5} aria-hidden />
          </button>
        </div>
      ))}
    </div>
  );
};
AttachmentStrip.displayName = "AttachmentStrip";
